import * as React from "react";

import { cn } from "../lib/utils";
import { useFieldControl } from "./field";
import { controlState, SELF_FRAME } from "./input";

/**
 * v2 Textarea (redesign phase 5, #455). Replaces both the Venus Textarea and
 * the shared AutosizeTextarea (break #46). Border, focus, invalid, read-only
 * and disabled match Input (classes shared from input.tsx); no slots, so the
 * textarea is its own frame.
 *
 * `rows` (default 3) is the height, and with `autosize` the minimum; the field
 * then grows with its content up to `maxRows` and scrolls beyond. Resize:
 * vertical only by hand, none with autosize (the height is managed).
 *
 * Autosize measures like v1: collapse, read `scrollHeight`, set the height.
 * Row limits come from the computed line height, padding and border, so they
 * are row-exact. Runs on every `value` change, on input (uncontrolled), when
 * the width changes (the text re-wraps) and once the web fonts have loaded.
 */

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  autosize?: boolean;
  /** Only with `autosize`: the height cap in rows. */
  maxRows?: number;
}

const px = (value: string, fallback = 0) => {
  const n = parseFloat(value);
  return Number.isNaN(n) ? fallback : n;
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(rawProps, ref) {
  const { autosize = false, maxRows = 10, rows = 3, className, onInput, ...props } = useFieldControl(rawProps);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  React.useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

  const resize = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el || !autosize) return;
    const style = getComputedStyle(el);
    const lineHeight = px(style.lineHeight, 20);
    const padding = px(style.paddingTop) + px(style.paddingBottom);
    const border = px(style.borderTopWidth) + px(style.borderBottomWidth);
    // border-box: the height includes padding and border; scrollHeight includes padding only.
    const min = rows * lineHeight + padding + border;
    const max = Math.max(maxRows, rows) * lineHeight + padding + border;
    el.style.height = "auto";
    const content = el.scrollHeight + border;
    el.style.height = `${Math.min(Math.max(content, min), max)}px`;
    // Hidden below the cap: sub-pixel rounding would otherwise flash a scrollbar.
    el.style.overflowY = content > max ? "auto" : "hidden";
  }, [autosize, rows, maxRows]);

  React.useLayoutEffect(resize, [resize, props.value]);

  // The first measurement can run on the fallback font, which wraps
  // differently; once the web fonts are in, measure again (no width change,
  // so the ResizeObserver below would not catch it).
  React.useEffect(() => {
    if (!autosize || typeof document === "undefined" || !document.fonts) return;
    let active = true;
    void document.fonts.ready.then(() => {
      if (active) resize();
    });
    return () => {
      active = false;
    };
  }, [autosize, resize]);

  React.useEffect(() => {
    const el = textareaRef.current;
    if (!el || !autosize || typeof ResizeObserver === "undefined") return;
    let width = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      // Our own height change triggers the observer too; only a new width re-wraps.
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      resize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [autosize, resize]);

  const state = controlState({ disabled: props.disabled, readOnly: props.readOnly, invalid: props["aria-invalid"] });

  return (
    <textarea
      ref={textareaRef}
      rows={rows}
      className={cn(
        "block w-full rounded-md border bg-transparent px-3 py-1.5 font-body text-body text-foreground transition-colors placeholder:text-muted-foreground",
        // Text wraps, so there is never anything to scroll sideways. Without this, a
        // non-visible overflow-y makes overflow-x compute to auto, and with "always
        // show scrollbars" (macOS) a horizontal bar eats the line the height was sized for.
        "overflow-x-hidden",
        autosize ? "resize-none" : "resize-y",
        SELF_FRAME[state],
        className,
      )}
      onInput={(event) => {
        resize();
        onInput?.(event);
      }}
      {...props}
    />
  );
});

export { Textarea };
